/**
 * Parses a date string or Date object into a Date object, ensuring UTC.
 * If a string is provided, it ensures the date is treated as UTC by appending 'Z' if it's not already present.
 * @param date - The date to parse, either as a string or a Date object.
 * @returns A Date object.
 */
export function parseDate(date: string | Date): Date {
  if (typeof date === 'string') {
    const stringDate = date + (date.endsWith('Z') ? '' : 'Z'); // Ensure UTC
    return new Date(stringDate);
  }

  return date;
}

/**
 * Interface for translation functions used in time ago formatting
 */
export interface TimeAgoTranslations {
  /** Function that returns translation for "X seconds ago" */
  secondsAgo: (seconds: number) => string;
  /** Function that returns translation for "X minutes ago" */
  minutesAgo: (minutes: number) => string;
  /** Function that returns translation for "X hours ago" */
  hoursAgo: (hours: number) => string;
  /** Function that returns translation for "X days ago" */
  daysAgo: (days: number) => string;
  /** Function that returns translation for "X weeks ago" */
  weeksAgo: (weeks: number) => string;
  /** Function that returns translation for "X months ago" */
  monthsAgo: (months: number) => string;
  /** Function that returns translation for "X years ago" */
  yearsAgo: (years: number) => string;
}

/**
 * Converts a date to a human-readable "time ago" string.
 * @param date - The date to convert, either as a string or a Date object.
 * @param translations - Optional translation functions for internationalization
 * @returns A string representing the time elapsed since the date (e.g., "5m ago").
 */
export function timeAgo(date: string | Date, translations: TimeAgoTranslations): string {
  const dateObj = parseDate(date);
  const now = new Date();
  const diffMs = now.getTime() - dateObj.getTime();
  const diffSeconds = Math.round(diffMs / 1000);
  const diffMinutes = Math.round(diffSeconds / 60);
  const diffHours = Math.round(diffMinutes / 60);
  const diffDays = Math.round(diffHours / 24);
  const diffWeeks = Math.round(diffDays / 7);
  const diffMonths = Math.round(diffDays / 30.44); // Average days in a month
  const diffYears = Math.round(diffDays / 365.25); // Account for leap years

  // Use translations if provided, otherwise fall back to English
  if (translations) {
    if (diffSeconds < 60) return translations.secondsAgo(diffSeconds);
    if (diffMinutes < 60) return translations.minutesAgo(diffMinutes);
    if (diffHours < 24) return translations.hoursAgo(diffHours);
    if (diffDays < 7) return translations.daysAgo(diffDays);
    if (diffWeeks < 5) return translations.weeksAgo(diffWeeks);
    if (diffMonths < 12) return translations.monthsAgo(diffMonths);
    return translations.yearsAgo(diffYears);
  }

  // Default English fallback
  if (diffSeconds < 60) return `${diffSeconds}s ago`;
  if (diffMinutes < 60) return `${diffMinutes}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  if (diffWeeks < 5) return `${diffWeeks}w ago`;
  if (diffMonths < 12) return `${diffMonths}mo ago`;
  return `${diffYears}y ago`;
}

/**
 * Interface for translation functions used in date formatting
 */
export interface DateTranslations {
  /** Translation for "today" */
  today: string;
  /** Translation for "1 day ago" */
  oneDayAgo: string;
  /** Function that returns translation for "X days ago" */
  daysAgo: (days: number) => string;
  /** Function that returns translation for "X weeks ago" */
  weeksAgo: (weeks: number) => string;
  /** Translation for "never" */
  never: string;
}

/**
 * Formats a date based on the variant and locale with translation support.
 * @param date - The date to format, null for "never" case
 * @param variant - The formatting variant ('overview' or 'default')
 * @param locale - The locale for Intl.DateTimeFormat (e.g., 'en-GB', 'it-IT')
 * @param translations - Translation functions for relative dates
 * @returns Formatted date string
 */
export function formatDate(date: Date | string | null, variant: 'overview' | 'default', locale: string, translations: DateTranslations): string {
  if (!date) return translations.never;

  const dateObj = parseDate(date);

  if (variant === 'overview') {
    // Use locale-specific format for overview variant
    return new Intl.DateTimeFormat(locale, {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    }).format(dateObj);
  }

  // Default variant - relative date format
  const now = new Date();
  const diffMs = now.getTime() - dateObj.getTime();
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  if (diffDays === 0) return translations.today;
  if (diffDays === 1) return translations.oneDayAgo;
  if (diffDays < 7) return translations.daysAgo(diffDays);
  if (diffDays < 30) return translations.weeksAgo(Math.floor(diffDays / 7));

  // Fall back to locale-specific date format for older dates
  return dateObj.toLocaleDateString(locale);
}
